import { View, Text, Pressable, Linking } from "react-native";
import { ScreenContainer, ScrollableContainer } from "../../components";
import { ScreenProps } from "../types";
import { colors } from "../../../theme";
import React from "react";
import { Feather } from "@expo/vector-icons";
import { useQuery, gql } from "@apollo/client";
import { CURRENT_PERSON_FRAGMENT } from "../fragments";
import ItemCard from "../../components/ItemCard";
import { InvoiceLoader } from "../HomeScreen/SkeletonLoaders";

interface Order {
  id: string;
  balance: number;
  createdAt: string;
  description: string;
  payable: boolean;
  status: string;
  total: number;
  type: string;
  pdfUrl?: string;
}

export const GET_ORDERS_QUERY = gql`
  query GetOrders {
    currentPerson {
      ...CurrentPersonFragment
      orders {
        id
        balance
        createdAt
        description
        payable
        status
        total
        type
        pdfUrl
      }
    }
  }
  ${CURRENT_PERSON_FRAGMENT}
`;

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString();
};

const getOrderBadge = (order: Order) => {
  if (order.status === "canceled") {
    return (
      <View className="flex-row items-center">
        <Feather name="x-circle" size={14} color={colors.red.DEFAULT} className="mr-1" />
        <Text className="text-red-500 text-sm font-medium">Canceled</Text>
      </View>
    );
  }

  if (order.status === "paid") {
    return (
      <View className="flex-row items-center">
        <Feather name="check-circle" size={14} color={colors.green.DEFAULT} className="mr-1" />
        <Text className="text-green-500 text-sm font-medium">Paid</Text>
      </View>
    );
  }

  return (
    <View className="flex-row items-center">
      <Feather name="alert-circle" size={14} color={colors.yellow.DEFAULT} className="mr-1" />
      <Text className="text-yellow-500 text-sm font-medium">Payment Required</Text>
    </View>
  );
};

const InvoiceListScreen = ({ navigation }: ScreenProps<"InvoiceList">) => {
  const { data, loading, error } = useQuery(GET_ORDERS_QUERY, {
    context: {
      callerWillHandleError: () => true,
    },
  });
  const orders: Order[] = data?.currentPerson?.orders || [];
  const cutoffDate = new Date("2023-12-31");

  const outstandingOrders = orders.filter(
    (order) => order.payable && order.balance > 0 && new Date(order.createdAt) > cutoffDate
  );

  if (loading) {
    return (
      <ScreenContainer navigationTitle="Outstanding Invoices">
        <View className="p-4">
          <InvoiceLoader />
        </View>
      </ScreenContainer>
    );
  }

  if (error) {
    return (
      <ScreenContainer navigationTitle="Outstanding Invoices">
        <View className="flex-1 items-center justify-center p-4">
          <Feather name="alert-circle" size={24} color={colors.red.DEFAULT} className="mb-2" />
          <Text className="text-regular-tight text-center text-red-500 mb-2 mt-2">Unable to load your invoices</Text>
          <Text className="text-regular-tight text-center text-gray-500">
            Please try again later or contact support if the problem persists.
          </Text>
        </View>
      </ScreenContainer>
    );
  }

  if (outstandingOrders.length === 0) {
    return (
      <ScreenContainer navigationTitle="Outstanding Invoices">
        <View className="flex-1 items-center justify-center py-8">
          <Feather name="check-circle" size={48} color={colors.green.DEFAULT} className="mb-4" />
          <Text className="text-regular-tight font-medium text-center text-gray-500 mb-2">You're all caught up!</Text>
          <Text className="text-regular-tight text-center text-gray-500">You have no outstanding invoices at this time.</Text>
        </View>
      </ScreenContainer>
    );
  }

  const handleClick = async (order: Order) => {
    if (order.payable) {
      const paymentUrl = order.type === "ccms" ? `https://payments.pga.org?invoiceNumbers=${order.id}` : "/member/dues";

      if (paymentUrl === "/member/dues") {
        navigation.navigate("Dues");
      } else {
        try {
          const supported = await Linking.canOpenURL(paymentUrl);
          if (supported) {
            await Linking.openURL(paymentUrl);
          }
        } catch (error) {
          console.error("An error occurred:", error);
        }
      }
    }
  };

  return (
    <ScreenContainer navigationTitle="Outstanding Invoices">
      <ScrollableContainer>
        <View className="p-4">
          {outstandingOrders.map((order) => (
            <ItemCard key={order.id} variant="default" onClick={() => handleClick(order)} badge={getOrderBadge(order)}>
              <ItemCard.Header>{formatDate(order.createdAt)}</ItemCard.Header>
              <ItemCard.Content>
                <View className="flex-1">
                  <Text className="text-lg font-medium mb-2">{order.description || `Order ${order.id}`}</Text>
                </View>
                <View className="ml-auto">
                  {order.payable && (
                    <Pressable onPress={() => handleClick(order)}>
                      <Feather name="credit-card" size={20} color={colors.primary.DEFAULT} />
                    </Pressable>
                  )}
                </View>
              </ItemCard.Content>
              <ItemCard.Footer>${(order.total / 100).toFixed(2)}</ItemCard.Footer>
            </ItemCard>
          ))}
        </View>
      </ScrollableContainer>
    </ScreenContainer>
  );
};

export default InvoiceListScreen;
